import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {useIntl} from 'react-intl';
import moment from 'moment';
import {Button, Checkbox, Detail, EMPHASIS, Label, Pill, PillVariant, Placeholder, Text} from '@forecast-it/design-system';
import Util from '../../../forecast-app/shared/util/util';
import {
	UnlinkedXeroInvoice,
	UnlinkedXeroInvoices,
	UnlinkedXeroOrganisation,
} from '../../../components/insights/invoicing/importFromXero';
import {
	CheckboxCell,
	EmptyState,
	InvoiceCell,
	ListBox,
	ListHeader,
	ListRow,
	LineAction,
	LinesBox,
	MatchCell,
	MatchName,
	MatchSkeleton,
	MessageLine,
	MessageText,
	ValueCell,
	RowWindow,
	SkeletonList,
	SkeletonRow,
} from './XeroInvoiceImportModal.styled';
import {CanSelect, invoiceKey, SelectableInvoice, SelectedInvoices} from './invoiceSelection';
import {MatchDisplay} from './clientMatch';

export type UnlinkedListState = {state: 'loading'} | {state: 'error'} | {state: 'ready'; data: UnlinkedXeroInvoices};

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const organisationName = (org: UnlinkedXeroOrganisation, names: Map<string, string>, formatMessage: FormatMessage): string =>
	names.get(org.tenantId) || formatMessage({id: 'xero_invoice_import.unknown_organisation'});

/** The line shown for an organisation that could not be listed. The modal's status region says the same. */
export const organisationMessage = (
	org: UnlinkedXeroOrganisation,
	names: Map<string, string>,
	isAdmin: boolean,
	formatMessage: FormatMessage
): string => {
	const organisation = organisationName(org, names, formatMessage);
	if (org.status === 'inaccessible') {
		return formatMessage(
			{id: isAdmin ? 'xero_invoice_import.org_inaccessible_admin' : 'xero_invoice_import.org_inaccessible_contact_admin'},
			{organisation}
		);
	}
	if (org.status === 'tooMany') {
		return formatMessage({id: 'xero_invoice_import.org_too_many'}, {organisation});
	}
	return formatMessage({id: 'xero_invoice_import.org_load_failed'}, {organisation});
};

const SKELETON_ROWS = 6;
// A row is 52 px or more, with 8 px between rows.
const ROW_GAP = 8;
const ESTIMATED_ROW_HEIGHT = 69;
// Rows drawn beyond the view on each side; also covers the header and organisation lines above the rows,
// which the window does not measure.
const OVERSCAN = 10;
const DATE_FORMAT = {day: 'numeric', month: 'short', year: 'numeric'} as const;

/**
 * The window's row measure, with the gap below each row. A row grows when its Client match arrives,
 * and the window's ResizeObserver reports it: re-measuring inside that callback moves and mounts rows
 * while the browser is still delivering the same round of resizes, which it reports as "ResizeObserver
 * loop completed with undelivered notifications" (the dev server's overlay then covers the page). So a
 * size the observer reports is taken on the next frame; until then the row keeps its last size. A row's
 * first measure, from its ref, is taken at once.
 */
export const measureRowOnNextFrame = () => {
	const sizes = new WeakMap<Element, number>();
	return (
		element: Element,
		entry: ResizeObserverEntry | undefined,
		instance: {measureElement: (element: Element) => void}
	): number => {
		const last = sizes.get(element);
		if (entry !== undefined && last !== undefined) {
			window.requestAnimationFrame(() => instance.measureElement(element));
			return last;
		}
		const size = element.getBoundingClientRect().height + ROW_GAP;
		sizes.set(element, size);
		return size;
	};
};

type Props = {
	list: UnlinkedListState;
	// Organisation names by tenant id, for the lines about organisations that could not be listed.
	organisationNames: Map<string, string>;
	// Can manage account settings, so can reconnect an organisation.
	isAdmin: boolean;
	onRetry: () => void;
	onReconnect: () => void;
	// The ticked invoices, from any range or organisation.
	selected: SelectedInvoices;
	// The header checkbox: none, some or every visible row ticked.
	headerState: boolean | 'indeterminate';
	// Something is ticked, here or outside the view.
	hasSelection: boolean;
	canSelect: CanSelect;
	onToggle: (row: SelectableInvoice) => void;
	onToggleVisible: () => void;
	// What each row's Client match cell shows.
	matchOf: (row: SelectableInvoice) => MatchDisplay;
	// Can create clients: a New client row says it will create one, else that it needs the permission.
	canCreateClients: boolean;
};

type RowProps = {
	invoice: SelectableInvoice;
	// Position in the list, and the row's offset in the window.
	index: number;
	start: number;
	measure: (element: HTMLElement | null) => void;
	checked: boolean;
	selectable: boolean;
	number: string;
	checkboxLabel: string;
	amountText: string;
	dateText: string;
	matchState: MatchDisplay['state'];
	// The Forecast client under a Matched pill, or the line under a New client pill; else empty.
	matchText: string;
	// The id of the permission line that says why the checkbox is disabled; else undefined.
	reasonId: string | undefined;
	onToggle: (row: SelectableInvoice) => void;
};

type MatchCellProps = {state: MatchDisplay['state']; text: string; textId: string | undefined};

// The Client match cell. Review shows nothing: the review step on Create (US-06) asks about it.
const ClientMatchCell = ({state, text, textId}: MatchCellProps): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<MatchCell role="cell" data-cy="xero-import-client-match">
			{state === 'checking' ? (
				<MatchSkeleton role="img" aria-label={formatMessage({id: 'xero_invoice_import.checking_client_match'})}>
					<Placeholder width="100%" height="24px" />
				</MatchSkeleton>
			) : null}
			{state === 'matched' ? (
				<>
					<Pill variant={PillVariant.SUCCESS}>{formatMessage({id: 'xero_invoice_import.client_matched'})}</Pill>
					<MatchName title={text}>
						<Detail color="medium" noWrap>
							{text}
						</Detail>
					</MatchName>
				</>
			) : null}
			{state === 'newClient' ? (
				<>
					<Pill variant={PillVariant.INFORMATION}>{formatMessage({id: 'xero_invoice_import.client_new'})}</Pill>
					<div id={textId}>
						<Detail color="medium">{text}</Detail>
					</div>
				</>
			) : null}
			{state === 'unknown' ? (
				<Detail color="medium">{formatMessage({id: 'xero_invoice_import.client_match_unknown'})}</Detail>
			) : null}
		</MatchCell>
	);
};

// Memoised, and given only strings and a stable onToggle, so ticking one row re-draws that row alone:
// a list can hold up to 2,000 invoices.
const InvoiceRow = React.memo(
	({
		invoice,
		index,
		start,
		measure,
		checked,
		selectable,
		number,
		checkboxLabel,
		amountText,
		dateText,
		matchState,
		matchText,
		reasonId,
		onToggle,
	}: RowProps): JSX.Element => (
		<ListRow
			role="row"
			aria-rowindex={index + 2}
			data-index={index}
			ref={measure}
			style={{transform: `translateY(${start}px)`}}
			data-cy="xero-import-row"
		>
			<CheckboxCell role="cell">
				{/* The checkbox is the only click target, named after the invoice and its contact: two
				    organisations can both have an INV-0001. */}
				<Checkbox
					aria-label={checkboxLabel}
					aria-describedby={reasonId}
					checked={checked}
					disabled={!selectable}
					onChange={() => onToggle(invoice)}
				/>
			</CheckboxCell>
			<InvoiceCell role="cell">
				<span title={number}>
					{/* ellipsis alone: with noWrap the clamp clips without the "…" */}
					<Label ellipsis>{number}</Label>
				</span>
				<span title={invoice.contactName}>
					<Text color="auxiliary" ellipsis>
						{invoice.contactName}
					</Text>
				</span>
			</InvoiceCell>
			<ClientMatchCell state={matchState} text={matchText} textId={reasonId} />
			<ValueCell role="cell">
				<Text noWrap>{amountText}</Text>
			</ValueCell>
			<ValueCell role="cell">
				<Text noWrap>{dateText}</Text>
			</ValueCell>
		</ListRow>
	)
);

type InvoiceRowsProps = {
	invoices: SelectableInvoice[];
	// The scrolling list box the rows sit in.
	listBoxRef: React.RefObject<HTMLDivElement>;
	selected: SelectedInvoices;
	canSelect: CanSelect;
	onToggle: (row: SelectableInvoice) => void;
	amount: (invoice: UnlinkedXeroInvoice) => string;
	matchOf: (row: SelectableInvoice) => MatchDisplay;
	canCreateClients: boolean;
};

/**
 * The rows, windowed: a list can hold up to 2,000 invoices, and drawing every row makes each tick slow,
 * so only the rows near the view are drawn. Mounted with each loaded list (the list shows a skeleton
 * while it loads), so every list starts a fresh window at the top: a kept window would scroll a new
 * list to where the last one was. A row scrolled far out of the window is unmounted, so a checkbox
 * that had focus there loses it; Tab still reaches the drawn rows.
 */
const InvoiceRows = ({
	invoices,
	listBoxRef,
	selected,
	canSelect,
	onToggle,
	amount,
	matchOf,
	canCreateClients,
}: InvoiceRowsProps): JSX.Element => {
	const {formatMessage, formatDate} = useIntl();
	const rowWindowRef = useRef<HTMLDivElement>(null);
	// Where the rows start in the list box, below the header and any organisation lines.
	const [scrollMargin, setScrollMargin] = useState(0);
	useLayoutEffect(() => {
		setScrollMargin(rowWindowRef.current?.offsetTop ?? 0);
	}, []);
	const [measureRowSize] = useState(measureRowOnNextFrame);
	const virtualizer = useVirtualizer({
		count: invoices.length,
		getScrollElement: () => listBoxRef.current,
		getItemKey: index => invoiceKey(invoices[index]),
		estimateSize: () => ESTIMATED_ROW_HEIGHT + ROW_GAP,
		measureElement: measureRowSize,
		overscan: OVERSCAN,
		scrollMargin,
	});
	// Stable, so the memoised rows still re-draw only when their own tick changes.
	const virtualizerRef = useRef(virtualizer);
	virtualizerRef.current = virtualizer;
	const measureRow = useCallback((element: HTMLElement | null) => virtualizerRef.current.measureElement(element), []);
	const matchTextOf = (invoice: SelectableInvoice, match: MatchDisplay): string => {
		if (match.state === 'matched') {
			return formatMessage({id: 'xero_invoice_import.forecast_client'}, {name: match.clientName});
		}
		if (match.state === 'newClient') {
			return canCreateClients
				? formatMessage({id: 'xero_invoice_import.new_client_helper'}, {contact: invoice.contactName})
				: formatMessage({id: 'xero_invoice_import.new_client_no_permission'});
		}
		return '';
	};

	return (
		// Every row is measured with the gap below it; the last row has none.
		<RowWindow ref={rowWindowRef} style={{height: Math.max(0, virtualizer.getTotalSize() - ROW_GAP)}}>
			{virtualizer.getVirtualItems().map(item => {
				const invoice = invoices[item.index];
				const number = invoice.invoiceNumber || formatMessage({id: 'xero_invoice_import.no_invoice_number'});
				const match = matchOf(invoice);
				const selectable = canSelect(invoice);
				// Without CLIENT_CREATE the New client line is why the row's checkbox is disabled.
				const reasonId =
					!selectable && !canCreateClients && match.state === 'newClient'
						? `xero-import-match-reason-${invoiceKey(invoice)}`
						: undefined;
				return (
					<InvoiceRow
						key={invoiceKey(invoice)}
						invoice={invoice}
						index={item.index}
						start={item.start - scrollMargin}
						measure={measureRow}
						checked={selected.has(invoiceKey(invoice))}
						selectable={selectable}
						number={number}
						checkboxLabel={
							invoice.invoiceNumber
								? formatMessage(
										{id: 'xero_invoice_import.select_invoice'},
										{invoiceNumber: invoice.invoiceNumber, contactName: invoice.contactName}
								  )
								: formatMessage(
										{id: 'xero_invoice_import.select_invoice_no_number'},
										{contactName: invoice.contactName}
								  )
						}
						amountText={amount(invoice)}
						dateText={formatDate(moment(invoice.date, 'YYYY-MM-DD').toDate(), DATE_FORMAT)}
						matchState={match.state}
						matchText={matchTextOf(invoice, match)}
						reasonId={reasonId}
						onToggle={onToggle}
					/>
				);
			})}
		</RowWindow>
	);
};

/**
 * The list area of the import modal: a skeleton while loading, the unlinked Xero invoices, a line for
 * each organisation that could not be listed, or the empty state. "No new invoices" is only said when
 * every organisation answered: an organisation that was not read is never reported as empty.
 */
export const UnlinkedInvoiceList = ({
	list,
	organisationNames,
	isAdmin,
	onRetry,
	onReconnect,
	selected,
	headerState,
	hasSelection,
	canSelect,
	onToggle,
	onToggleVisible,
	matchOf,
	canCreateClients,
}: Props): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const listBoxRef = useRef<HTMLDivElement>(null);

	if (list.state === 'loading') {
		return (
			<SkeletonList data-cy="xero-import-list-loading">
				{Array.from({length: SKELETON_ROWS}, (_, index) => (
					<SkeletonRow key={index} data-cy="xero-import-skeleton-row">
						<Placeholder width="100%" height="52px" />
					</SkeletonRow>
				))}
			</SkeletonList>
		);
	}

	// organisation names the button after it: several organisation lines can each have one.
	const tryAgain = (organisation?: string): JSX.Element => (
		<Button
			emphasis={EMPHASIS.LOW}
			onPress={onRetry}
			data-cy="xero-import-try-again"
			aria-label={organisation ? formatMessage({id: 'xero_invoice_import.try_again_for'}, {organisation}) : undefined}
		>
			{formatMessage({id: 'xero_invoice_import.try_again'})}
		</Button>
	);

	if (list.state === 'error') {
		return (
			<ListBox data-cy="xero-import-list-error">
				<MessageLine>
					<MessageText>
						<Text>{formatMessage({id: 'xero_invoice_import.load_failed'})}</Text>
					</MessageText>
					<LineAction data-cy="xero-import-line-action">{tryAgain()}</LineAction>
				</MessageLine>
			</ListBox>
		);
	}

	const {organisations, invoices} = list.data;
	const notListed = organisations.filter(org => org.status !== 'ok');

	const organisationLine = (org: UnlinkedXeroOrganisation): JSX.Element => {
		const organisation = organisationName(org, organisationNames, formatMessage);
		const message = organisationMessage(org, organisationNames, isAdmin, formatMessage);
		let action: JSX.Element | null = null;
		if (org.status === 'inaccessible') {
			// Trying again cannot fix a revoked or expired connection; only reconnecting does.
			action = isAdmin ? (
				<Button emphasis={EMPHASIS.LOW} onPress={onReconnect} data-cy="xero-import-reconnect">
					{formatMessage({id: 'common.reconnect'})}
				</Button>
			) : null;
		} else if (org.status !== 'tooMany') {
			action = tryAgain(organisation);
		}
		return (
			<MessageLine key={org.tenantId} data-cy={`xero-import-org-line-${org.tenantId}`}>
				<MessageText>
					<Text>{message}</Text>
				</MessageText>
				{action ? <LineAction data-cy="xero-import-line-action">{action}</LineAction> : null}
			</MessageLine>
		);
	};

	if (invoices.length === 0 && notListed.length === 0) {
		return (
			<EmptyState data-cy="xero-import-list-empty">
				<Label>{formatMessage({id: 'xero_invoice_import.empty_title'})}</Label>
				<Text color="auxiliary">{formatMessage({id: 'xero_invoice_import.empty_hint'})}</Text>
				{hasSelection ? (
					<Text color="auxiliary">{formatMessage({id: 'xero_invoice_import.empty_hint_ticked'})}</Text>
				) : null}
			</EmptyState>
		);
	}

	// A symbol two currencies in the list share ($ for USD, AUD, NZD) says nothing: show the code then.
	const symbolOf = (currencyCode: string): string => Util.GetCurrencySymbol(currencyCode) || currencyCode;
	const codesBySymbol = new Map<string, Set<string>>();
	invoices.forEach(invoice => {
		const symbol = symbolOf(invoice.currencyCode);
		codesBySymbol.set(symbol, (codesBySymbol.get(symbol) || new Set<string>()).add(invoice.currencyCode));
	});
	const amount = (invoice: UnlinkedXeroInvoice): string => {
		const symbol = symbolOf(invoice.currencyCode);
		const shared = (codesBySymbol.get(symbol)?.size ?? 0) > 1;
		return Util.getFormattedNumberWithCurrency(shared ? invoice.currencyCode : symbol, invoice.total, intl);
	};

	return (
		<ListBox ref={listBoxRef} data-cy="xero-import-list-ready">
			{notListed.length > 0 ? (
				<LinesBox data-cy="xero-import-org-lines">{notListed.map(organisationLine)}</LinesBox>
			) : null}
			{invoices.length > 0 ? (
				<LinesBox
					role="table"
					aria-label={formatMessage({id: 'xero_invoice_import.table_label'})}
					aria-rowcount={invoices.length + 1}
				>
					<ListHeader role="row" aria-rowindex={1} data-cy="xero-import-list-header">
						<CheckboxCell role="columnheader">
							{/* Ticks or unticks the rows shown, never those outside the view. */}
							<Checkbox
								aria-label={formatMessage({id: 'xero_invoice_import.select_all_shown'})}
								checked={headerState}
								// Nothing shown can be ticked (without CLIENT_CREATE: all New client, review or checking).
								disabled={!invoices.some(canSelect)}
								onChange={onToggleVisible}
							/>
						</CheckboxCell>
						<div role="columnheader">
							<Text color="auxiliary">{formatMessage({id: 'xero_invoice_import.column_invoice'})}</Text>
						</div>
						<div role="columnheader">
							<Text color="auxiliary">{formatMessage({id: 'xero_invoice_import.client_match'})}</Text>
						</div>
						<ValueCell role="columnheader">
							<Text color="auxiliary">{formatMessage({id: 'common.amount'})}</Text>
						</ValueCell>
						<ValueCell role="columnheader">
							<Text color="auxiliary">{formatMessage({id: 'common.date'})}</Text>
						</ValueCell>
					</ListHeader>
					<InvoiceRows
						invoices={invoices}
						listBoxRef={listBoxRef}
						selected={selected}
						canSelect={canSelect}
						onToggle={onToggle}
						amount={amount}
						matchOf={matchOf}
						canCreateClients={canCreateClients}
					/>
				</LinesBox>
			) : null}
		</ListBox>
	);
};
